import Component from "../../lib/Component.js";
import JustWatchRating from "./JustWatchRating.js";
import JustWatchWidget from "./JustWatchWidget.js";

const justWatchDataPromiseCache = new Map();

export const fetchJustWatchData = async (idType, id, apiKey) => {
  const cacheKey = `${idType}:${id}`;
  if (!justWatchDataPromiseCache.has(cacheKey)) {
    const fetchData = async () => {
      let url = `/api/justwatch/${idType}/${id}`;
      if (apiKey) {
        url += `?token=${apiKey}`;
      }
      try {
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          return data;
        }
      } catch {
        justWatchDataPromiseCache.delete(cacheKey); // Clear cache on failure to allow retrying later
        if (process.env.NODE_ENV === "development") {
          console.warn(`Failed to fetch JustWatch data for ${cacheKey}`);
        }
      }
      return null;
    };
    justWatchDataPromiseCache.set(cacheKey, fetchData());
  }
  return justWatchDataPromiseCache.get(cacheKey);
};

const JustWatchAPI = ({ ratingSelector, widgetSelector, preloadScriptId }) => {
  // Scan for all rating and widget placeholders on the page
  const ratingElements = document.querySelectorAll(ratingSelector);
  const widgetInnerElements = document.querySelectorAll("[data-jw-widget]");

  // Early return if no placeholders found
  if (ratingElements.length === 0 && widgetInnerElements.length === 0) {
    return Component.noop();
  }

  // Index placeholders by id-idType combination
  const movieMap = new Map(); // key: "idType:id", value: { id, idType, apiKey, range, ratings: [], widgets: [] }

  // Index rating placeholders
  ratingElements.forEach((element) => {
    const id = element.getAttribute("data-id");
    const idType = element.getAttribute("data-id-type");
    const apiKey = element.getAttribute("data-api-key");
    const range = element.getAttribute("data-range") || "1d";

    if (id && idType && apiKey) {
      const key = `${idType}:${id}`;
      if (!movieMap.has(key)) {
        movieMap.set(key, { id, idType, apiKey, range, ratings: [], widgets: [] });
      }
      movieMap.get(key).ratings.push(element);
    }
  });

  // Index widget placeholders (read from inner [data-jw-widget] but store outer wrapper)
  widgetInnerElements.forEach((innerElement) => {
    const id = innerElement.getAttribute("data-id");
    const idType = innerElement.getAttribute("data-id-type");
    const apiKey = innerElement.getAttribute("data-api-key");

    // Get the outer wrapper element (.movie-justwatch)
    const wrapperElement = innerElement.closest(widgetSelector);

    if (id && idType && apiKey && wrapperElement) {
      const key = `${idType}:${id}`;
      if (!movieMap.has(key)) {
        movieMap.set(key, { id, idType, apiKey, range: "1d", ratings: [], widgets: [] });
      }
      movieMap.get(key).widgets.push(wrapperElement);
    }
  });

  const componentInstances = [];

  /** @typedef {"1d" | "7d" | "30d"} JustWatchRatingRange */

  /**
   * @param {{
   *   id: string,
   *   idType: "tmdb" | "imdb" | "justwatch",
   *   apiKey: string,
   *   range: JustWatchRatingRange
   * }} param0
   * @returns {Promise<{exists: boolean, data?: {ranks: Record<JustWatchRatingRange, {rank: number, delta: number}>, full_path: string}}>}
   */
  const checkMovieExists = async ({ id, idType, apiKey, range }) => {
    try {
      /** @type {{ranks: Record<JustWatchRatingRange, {rank: number, delta: number}>, full_path: string}|null} */
      const data = await fetchJustWatchData(idType, id, apiKey);

      if (data) {
        return { exists: true, data: { ...data, range } };
      }

      return { exists: false };
    } catch {
      return { exists: false };
    }
  };

  return new Component({
    onMount: async () => {
      // Fetch and render for each unique id-idType combination
      for (const [_key, movieData] of movieMap.entries()) {
        const result = await checkMovieExists({
          id: movieData.id,
          idType: movieData.idType,
          apiKey: movieData.apiKey,
          range: movieData.range,
        });

        if (result.exists && Object.hasOwnProperty.call(result.data?.ranks ?? {}, movieData.range)) {
          // Render rating components for this movie
          movieData.ratings.forEach((ratingElement) => {
            const ratingComponent = JustWatchRating({
              selector: null, // We'll pass the element directly
              element: ratingElement,
              data: result.data,
            });
            ratingComponent.render();
            componentInstances.push(ratingComponent);
          });
        }

        if (result.exists && result.data?.offers) {
          // Render widget components for this movie
          movieData.widgets.forEach((widgetElement) => {
            const widgetComponent = JustWatchWidget({
              widgetSelector: null,
              element: widgetElement,
              preloadScriptId,
            });
            widgetComponent.render();
            componentInstances.push(widgetComponent);
          });
        } else {
          // Movie doesn't exist - hide its widgets
          movieData.widgets.forEach((element) => {
            element.style.display = "none";
          });
        }
      }
    },
    onUnmount: () => {
      componentInstances.forEach((component) => {
        if (component && component.onUnmount) {
          component.onUnmount();
        }
      });
    },
  });
};

export default JustWatchAPI;
